import type {
  DocumentNode,
  LazyQueryHookOptions,
  OperationVariables,
  QueryTuple,
  TypedDocumentNode,
} from '@apollo/client';
import { useLazyQuery } from '@apollo/client';
import { useEffect } from 'react';

export function useDelayedQuery<TData, TVariables extends OperationVariables>(
  queryToRun: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: LazyQueryHookOptions<TData, TVariables>,
  startCountdown = true,
  secondsToDelay = 3
) {
  const [query, queryResponse] = useLazyQuery<TData, TVariables>(queryToRun, {
    ...options,
    fetchPolicy: options?.fetchPolicy ?? 'cache-first',
  });

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;

    if (startCountdown && !queryResponse.called) {
      timeout = setTimeout(() => {
        query();
      }, secondsToDelay * 1000);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [query, queryResponse.called, secondsToDelay, startCountdown]);

  return [query, queryResponse] as QueryTuple<TData, TVariables>;
}
